(ns app.front.view.application-wizard.subs
  (:require [app.domain.group.model :as group-model]
            [app.domain.licence.renewal-model :as renewal-model]
            [app.domain.sign-off.model :as sign-off-model]
            [app.domain.user.authority :as authority]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.view.application-wizard.navigation :as wizard-nav]
            [app.front.view.application.utils :as app-utils]
            [app.front.view.workspace.action-subs :as ws-action-subs]
            [app.front.view.workspace.util :as ws-util]
            [app.i18n.model :as i18n-model]
            [core.front.event :refer [>evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::group-id
  :<- [:route/match]
  #(-> % :parameters :query :group-id))

(defn workspace-id [match]
  (or (-> match :parameters :path :workspace-id)
      (-> match :parameters :query :workspace-id)))

(rf/reg-sub ::workspace-id
  :<- [:route/match]
  :-> workspace-id)

(rf/reg-sub ::workspace
  :<- [::entities/context]
  :<- [::workspace-id]
  (fn [[ctx workspace-id] _]
    (when workspace-id
      (entities/find-entity ctx {:workspace/id workspace-id}))))

(defn permit-type [match workspace]
  (or (-> match :parameters :query :permit-type)
      (:workspace/permit-type workspace)))

(rf/reg-sub ::permit-type
  :<- [:route/match]
  :<- [::workspace]
  (fn [[match workspace] _]
    (permit-type match workspace)))

(rf/reg-sub ::application
  :<- [::workspace]
  (fn [workspace _]
    (when workspace
      (workspace-model/workspace-application workspace))))

(rf/reg-sub ::renewal-workspace?
  :<- [::workspace]
  :-> renewal-model/renewal-workspace?)

(rf/reg-sub ::express-issuance-pending?
  ;; True while express issuance is running in the background after a successful submit:
  ;; the submit signal returns once the application reaches :process.state/permit-issuance-in-progress,
  ;; but the actual permit PDF generation (and the resulting transition to permit-issued) happens
  ;; asynchronously afterwards (see app.domain.express-issuance.process). Lets the wizard end screen
  ;; show an accurate "processing" state instead of "submitted" right before it flips to "issued".
  :<- [::application]
  (fn [application _]
    (= :process.state/permit-issuance-in-progress (:process/state application))))

(rf/reg-sub ::permit-type->groups
  :-> :app.front.view.application-wizard.core/permit-type->groups)


(rf/reg-sub ::permit-type-groups
  :<- [::permit-type->groups]
  :<- [::permit-type]
  (fn [[permit-type->groups permit-type] _]
    (get permit-type->groups permit-type)))

(defn authority-group-for-category [groups group-id category-id]
  (or (when category-id
        (p/find-first groups #(contains? (:category-gc-ids %) category-id)))
      (p/find-first groups {:db/id group-id})))

(rf/reg-sub ::authority-group
  :<- [::permit-type-groups]
  :<- [::group-id]
  :<- [:app.front.view.application-wizard.classification/selected-category-id]
  (fn [[groups group-id category-id]]
    (authority-group-for-category groups group-id category-id)))

(defn authority-group
  "Sync twin of the `::authority-group` sub for use in event handlers (which cannot subscribe)."
  [db]
  (let [{:keys [permit-type group-id]} (-> db :route/match :parameters :query)
        groups                         (-> db
                                           :app.front.view.application-wizard.core/permit-type->groups
                                           (get permit-type))
        category-id                    (:app.front.view.application-wizard.classification/category-id db)]
    (when (and groups group-id)
      (authority-group-for-category groups group-id category-id))))

(rf/reg-sub ::authority-user?
  :<- [:session/user]
  :<- [::workspace]
  :<- [::authority-group]
  (fn [[user workspace authority-group] _]
    (when-let [group (or authority-group (:domain/authority-group workspace))]
      (authority/authority-in-group? user group))))

;; Domain group of the authority group the wizard was entered with. Used as a fallback before a
;; category has been selected; see `domain-group-id`.
(rf/reg-sub ::entry-domain-group-id
  :-> :app.front.view.application-wizard.core/entry-domain-group-id)

(defn domain-group-id
  "Domain group that owns the GIS configuration to use (map layers, address and point search,
   property owners and attributes). A municipality can be served by several authority groups for the
   same permit type (e.g. a lower-tier and an upper-tier authority), each under its own domain group,
   so the domain group follows the authority group of the selected category — the same group the
   workspace is created under."
  [db]
  (or (:domain-group-id (authority-group db))
      (:app.front.view.application-wizard.core/entry-domain-group-id db)))

(rf/reg-sub ::domain-group-id
  :<- [::authority-group]
  :<- [::entry-domain-group-id]
  (fn [[authority-group entry-domain-group-id] _]
    (or (:domain-group-id authority-group)
        entry-domain-group-id)))

(rf/reg-sub ::domain-group
  :<- [::entities/context]
  :<- [::domain-group-id]
  :<- [::workspace]
  (fn [[ctx domain-group-id workspace] _]
    (or (when domain-group-id
          (entities/find-entity ctx {:group/id domain-group-id}))
        (some-> workspace
                group-model/get-authority-group
                group-model/get-authority+domain-group
                second))))

(rf/reg-sub ::tr
  :<- [::authority-group]
  :<- [::permit-type]
  :<- [:tr]
  (fn [[authority-group permit-type tr]]
    (-> (group-model/get-i18n-mapping authority-group)
        (merge (i18n-model/permit-type-specific-keys permit-type))
        (i18n-model/wrap-tr-fn tr))))

(rf/reg-sub ::auto-fill-enabled-for-group?
  :<- [::domain-group]
  :<- [::ff/enabled-flags]
  :<- [::authority-user?]
  (fn [[domain-group feature-flags authority?] _]
    (and domain-group
         (not (group-model/ai-auto-fill-disabled? domain-group feature-flags authority?)))))

(rf/reg-sub ::plan-check-enabled-for-group?
  :<- [::domain-group]
  :<- [::ff/enabled-flags]
  (fn [[domain-group feature-flags] _]
    (and domain-group
         (not (group-model/plan-check-disabled? domain-group feature-flags)))))

(rf/reg-sub ::check-wizard-ai-rules-allowed?
  :<- [::application]
  :-> #(-> % :granted-permissions :permission.phase/write boolean))

(def ^:private wizard-option-ids
  #{:process.signal/add-admin-write-authorization
    :process.signal/remove-admin-write-authorization
    :process.signal/sign-off-view-visit
    :process.signal/cancel-all-sign-offs})

(defn wizard-view-data [workspace-id options]
  {:options
   (update options :items
           (fn [items]
             (->> items
                  (filter #(wizard-option-ids (:id %)))
                  (map (fn [item]
                         (if (= :process.signal/sign-off-view-visit (:id item))
                           (assoc item :on-click #(>evt [::wizard-nav/redirect-to-sign-off workspace-id]))
                           item)))
                  vec)))})

(rf/reg-sub ::view-data
  :<- [::workspace-id]
  :<- [:app.front.view.workspace.route-subs/workspace-base-options :workspace.state/application]
  :<- [:route/match]
  :<- [::ff/enabled? :feature-flag.id/workspace-search-updates]
  (fn [[workspace-id options match search-updates?] _]
    (when workspace-id
      (p/assoc-if (wizard-view-data workspace-id options)
                  :back-href (when (and search-updates? (ws-util/search-origin-query-params match))
                               (href/href :view/search nil {:selected-tab "workspace"}))))))

(defn- wizard-actionable?
  "The wizard can only offer actions it knows how to perform: real FSM commands
  (:signal-type), or UI-only (:primary-action) signals it has a local handler for.
  An unrecognised UI-only signal is skipped so the wizard falls through to the next
  primary candidate rather than rendering a button that misroutes or does nothing.
  partial-submit is excluded: it flags the application for automatic submission once
  payment is confirmed, which is redundant on the wizard payment step."
  [{:keys [signal-id primary-action]}]
  (and (not= :process.signal/partial-submit signal-id)
       (or (not primary-action)
           (contains? wizard-nav/local-signal-handlers signal-id))))

(rf/reg-sub ::sign-off-done?
  :<- [::application]
  (fn [application _]
    (sign-off-model/any-sign-offs-done? (:application/sign-off-form application))))

(rf/reg-sub ::submit-action
  ;; Picks the single primary action to surface on the wizard summary/payment step.
  ;; Mirrors the workspace view's selection rule (app-utils/visible-primary-actions):
  ;; use the first visible (non-disabled) primary, or the first primary when all are
  ;; disabled so the user still sees the blocking errors.
  :<- [::ws-action-subs/actions [::process/application-process]]
  (fn [actions _]
    (let [actionable (filter wizard-actionable? actions)]
      (or (first (app-utils/visible-primary-actions actionable))
          (first (filter #(= :primary (:ui-type %)) actionable))))))
